import { Mutation, MutationCache, QueryClient } from '@tanstack/react-query'
import { shouldHandleError, shouldIgnoreError } from '../common/query'
import { kontrollpunktKeys } from '../features/kontrollpunkter/queries/helpers'
import { getDeltakerKeys } from '../features/deltakere/queries/helpers'
import { veiledningerKeys } from '../features/veiledninger/queries/helpers'
import { kvitteringQueryHelpers } from '../features/kvitteringer/queries/helpers'
import { metadataQueryHelpers } from '../features/bilder/queries/helpers'
import { captureException } from '@sentry/react'
import { CAPTURE_NAME, CaptureType } from '../sentry'
import { DeltakerTypes } from '../features/deltakere/types'
import { kvitteringStatusEnum } from '../ducks/kvittering/types'

/**
 * Invalid when retry is true. Will never be called.
 *
 * Keeping it for the time being. May be deleted.
 */
const onMutationError = (
  error: unknown,
  variables: unknown,
  context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>
) => {
  if (!shouldHandleError(error) || shouldIgnoreError(error)) {
    return
  }

  const errMessage = `${JSON.stringify(mutation.options.mutationKey)}: ${
    (error as Error)?.message
  }`
  const err = new Error(errMessage, {
    cause: error as Error,
  })

  Object.defineProperty(err, 'stack', {
    value: (error as Error).stack,
    writable: true,
    configurable: true,
  })

  // eslint-disable-next-line no-console
  console.error('[Mutation Error]:', err)

  captureException(err, {
    tags: { [CAPTURE_NAME]: CaptureType.MUTATION },
  })
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24 * 4, // 4 days
      staleTime: 1000 * 60 * 60, // 60 minutes
    },
    mutations: {
      gcTime: 1000 * 60 * 60 * 24 * 24, // 24 days
      retry: false,
      scope: { id: 'run-all-mutations-in-serial' }, // Offline support (see SYNK_CHANGELOG.md 2024-08-20)
    },
  },
  mutationCache: new MutationCache({
    onError: onMutationError,
  }),
})

const shouldIgnoreMutation = (mutationKey: string, kvitteringId: string) => {
  const ignoreIndex = {
    [JSON.stringify(kontrollpunktKeys.add(kvitteringId)[0])]: true,
    [JSON.stringify(veiledningerKeys.add(kvitteringId)[0])]: true,
    [JSON.stringify(getDeltakerKeys(DeltakerTypes.ANDRE).add(kvitteringId)[0])]:
      true,
    [JSON.stringify(
      getDeltakerKeys(DeltakerTypes.MATTILSYNET).add(kvitteringId)[0]
    )]: true,
    [JSON.stringify(
      getDeltakerKeys(DeltakerTypes.VIRKSOMHET).add(kvitteringId)[0]
    )]: true,
  }

  return ignoreIndex[mutationKey] ?? false
}

queryClient.getMutationCache().subscribe((event) => {
  if (!['added', 'removed', 'updated'].includes(event.type)) {
    return
  }

  const mutations = queryClient.getMutationCache().findAll({
    predicate: (mutation) => {
      const mutationKey = mutation.options.mutationKey?.[0] as any
      const shouldIgnore = shouldIgnoreMutation(
        JSON.stringify(mutationKey),
        mutationKey.kvitteringId
      )

      return (
        mutationKey.kvitteringId !== undefined &&
        !shouldIgnore &&
        ['pending', 'idle'].includes(mutation.state.status)
      )
    },
  })

  const mutationCount = mutations.length

  if (mutationCount !== 0) {
    return
  }

  const forberedteCache = kvitteringQueryHelpers.getForsideKvitteringerCache(
    (kvitteringer) =>
      kvitteringer.filter((k) => k.status === kvitteringStatusEnum.FORBEREDT)
  )
  const paabegynteCache = kvitteringQueryHelpers.getForsideKvitteringerCache(
    (kvitteringer) =>
      kvitteringer.filter((k) => k.status === kvitteringStatusEnum.PAABEGYNT)
  )

  const kvitteringer = [...forberedteCache, ...paabegynteCache]

  kvitteringer.forEach((kvittering) => {
    if (!kvittering.id) {
      return
    }

    void kvitteringQueryHelpers.invalidateKvittering(kvittering.id)
    void metadataQueryHelpers.invalidateMetadataList(kvittering.id)
  })
})

export const exportedForTesting = {
  onMutationError,
}
